import { Button, Table } from "antd";
import { ColumnsType } from "antd/es/table";
import { CheckboxProps } from "antd/lib/checkbox";
import React, {
  FunctionComponent,
  ReactText,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import {
  getCognitoUserData,
  resendConfirmationEmail,
} from "../../features/auth/authSlice";
import {
  fetchCompanies,
  selectCompanies,
  selectCompaniesAreFetching,
} from "../../features/company/companySlice";
import { fetchGroups, selectGroupsDict } from "../../features/groups/groupSlice";
import {
  disableUsersStatusNoRefetch,
  enableUsersStatusNoRefetch,
  IUser,
  RolesDict,
  UserRole,
  UsersGroups,
  deleteUserByIdNoRefetch,
  selectIsDeletingUser,
} from "../../features/users/usersSlice";
import {
  fetchUsersPaginated,
  selectAreUsersPaginatedFetching,
  selectUsersPaginatedList,
  selectUsersPaginatedPage,
  selectUsersPaginatedTotal,
  selectFilterTextForUsersPaginated,
  selectFilterCompanyIdForUsersPaginated,
  setFilterTextForUsersPaginated,
  setFilterCompanyIdForUsersPaginated,
} from "../../features/users/usersPaginatedSlice";
import { PAGE_SIZE } from "../../utils/constants";
import { Status, StatusEnum } from "../common/Status";
import tableStyles from "../common/table.module.scss";
import { TableTools } from "../common/TableTools";
import { CompanyFilter } from "../Filters/Company";
import { useDebounceSelector } from "../utils/useDebounce";
import { NamePlusThumbnail } from "./common/NamePlusThumbnail";
import { ActionSuffixes, Prefixes } from "../prefixes";
import { getTranslations } from "../../features/translations/translationsUtils";
import { DialogPopUpConfirmation } from "../common/DialogPopUpConfirmation";


export const UsersList: FunctionComponent = () => {
  const cognitoUserData = useSelector(getCognitoUserData);
  const companiesDict = useSelector(selectCompanies);
  const usersPaginatedList = useSelector(selectUsersPaginatedList);
  const groupsDict = useSelector(selectGroupsDict);
  const usersList = usersPaginatedList.map((user) => ({
    ...user,
    companyName: companiesDict[user.company ?? ""]?.name,
    studentGroupName: groupsDict[user.studentGroupId ?? ""]?.name,
  }));
  const page = useSelector(selectUsersPaginatedPage);
  const total = useSelector(selectUsersPaginatedTotal);
  const areCompaniesFetching = useSelector(selectCompaniesAreFetching);
  const areUsersFetching = useSelector(selectAreUsersPaginatedFetching);
  const filterValue = useSelector(selectFilterTextForUsersPaginated);
  const filteredInfo = useSelector(selectFilterCompanyIdForUsersPaginated);

  const debounceFilterValue = useDebounceSelector(selectFilterTextForUsersPaginated, 500);
  const history = useHistory();
  const [selectedRowKeys, selectRowKeys] = useState<ReactText[]>([]);
  const [showActivationBtns, setShowActivationBtns] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState('');
  const isDeletingUser = useSelector(selectIsDeletingUser);
  const dispatch = useDispatch();
  const role = cognitoUserData ? cognitoUserData["custom:role"] : undefined;
  const isSuperAdmin = role === UserRole.SuperAdmin;

  useEffect(() => {
    if (selectedRowKeys.length > 0) {
      setShowActivationBtns(true);
    } else {
      setShowActivationBtns(false);
    }
  }, [selectedRowKeys]);


  const onEdit = (userId: string) => {
    history.push(`${Prefixes.user}/${userId}${ActionSuffixes.edit}`);
  };

  const columns: ColumnsType<IUser> = [
    {
      title: getTranslations('LIST_FULL_NAME'),
      dataIndex: "name",
      sortDirections: ["descend", "ascend"],
      defaultSortOrder: "ascend",
      ellipsis: true,
      sorter: (a: IUser, b: IUser) => a.name.localeCompare(b.name),
      render: (value, user) => {
        return <NamePlusThumbnail value={value} user={user} />;
      },
    },
    {
      title: getTranslations('LIST_FULL_ROLE'),
      dataIndex: "role",
      render: (value, values) => {
        if (values.groups?.includes(UsersGroups.Administrators)) {
          return RolesDict[value as UserRole];
        }
        return 'Estudiante';
      },
      sortDirections: ["descend", "ascend"],
    },
    {
      title: getTranslations('LIST_STATUS'),
      dataIndex: "enabled",
      render: (status) => {
        return (
          <Status status={status ? StatusEnum.Enable : StatusEnum.Disable} />
        );
      },
      sortDirections: ["descend", "ascend"],
      sorter: (a: IUser, b: IUser) =>
        a.enabled === b.enabled ? 0 : a.enabled > b.enabled ? 1 : -1,
    },
    {
      title: getTranslations('LIST_GROUP'),
      dataIndex: "studentGroupName",
      ellipsis: true,
      sortDirections: ["descend", "ascend"],
      // filteredValue: filteredInfo.length > 1 ? filteredInfo : [],
      sorter: (a: any, b: any) =>
        (a.studentGroupName || "").localeCompare(b.studentGroupName || ""),
      onFilter: (value, record: IUser) =>
        record.studentGroupId === (value as string),
    },
    {
      title: getTranslations('LIST_COMPANY'),
      dataIndex: "companyName",
      sortDirections: ["descend", "ascend"],
      sorter: (a: IUser, b: IUser) =>
        (a.companyName || "").localeCompare(b.companyName || ""),
      render: (value, user) => {
        return (
          <React.Fragment>
            {value}
            <div className={tableStyles.actions}>
              {user.status === "FORCE_CHANGE_PASSWORD" ? (
                <Button
                  type="default"
                  onClick={() => {
                    dispatch(resendConfirmationEmail(user.username));
                  }}
                >
                  {getTranslations('USER_RESEND_INVITATION')}
                </Button>
              ) : null}
              <Button
                type="default"
                onClick={() => {
                  onEdit(user.username);
                }}
              >
                {getTranslations('LIST_EDIT')}
              </Button>
              {!isSuperAdmin || user.enabled ? null : <Button
                type="default"
                onClick={() => {
                  setUserToDelete(user.username);
                  setShowConfirmationModal(true);
                }}
              >
                {getTranslations('USER_DELETE')}
              </Button>}
            </div>
          </React.Fragment>
        );
      },
    },
  ];

  const debouncedText = debounceFilterValue[0] || undefined;

  useEffect(() => {
    dispatch(fetchCompanies());
    dispatch(fetchGroups());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchUsersPaginated({
      page: 1,
      limit: PAGE_SIZE,
      companyId: filteredInfo || undefined,
      text: debouncedText,
    }));
  }, [dispatch, debouncedText, filteredInfo]);

  const refetchUsers = () => {
    dispatch(fetchUsersPaginated({
      page,
      limit: PAGE_SIZE,
      companyId: filteredInfo || undefined,
      text: debouncedText,
    }));
  };

  const cognitoUsername = cognitoUserData
    ? cognitoUserData["cognito:username"]
    : null;
  const rowSelection = {
    getCheckboxProps: (record: IUser): CheckboxProps => {
      if (record.username === cognitoUsername) {
        return {
          disabled: true,
        };
      }
      return {};
    },
    selectedRowKeys,
    onChange: (selectedRowKeys: ReactText[]) => {
      selectRowKeys(selectedRowKeys);
    },
  };

  const enableStatus = async () => {
    const data = {
      usernames: selectedRowKeys.map((key) => key.toString()),
    };
    await dispatch(enableUsersStatusNoRefetch(true, data));
    refetchUsers();
  };

  const disableStatus = async () => {
    const data = {
      usernames: selectedRowKeys.map((key) => key.toString()),
    };
    await dispatch(disableUsersStatusNoRefetch(true, data));
    refetchUsers();
  };
  const setFilterValue = (value: string[]) => {
    dispatch(setFilterTextForUsersPaginated(value[0] || ""));
  }

  const setFilteredInfo = (value: string) => {
    dispatch(setFilterCompanyIdForUsersPaginated(value || ""));
  };

  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
    setUserToDelete('');
  };
  const handleOnDelete = async () => {
    setShowConfirmationModal(false);
    await dispatch(deleteUserByIdNoRefetch(userToDelete));
    setUserToDelete('');
    refetchUsers();
  };

  return (
    <React.Fragment>

      <DialogPopUpConfirmation
        name={getTranslations('USER_DELETE_CONFIRMATION')}
        visible={showConfirmationModal || isDeletingUser }
        onCancel={handleCloseConfirmationModal}
        cancelText={getTranslations('USER_DELETE_CANCEL')}
        okText={getTranslations('USER_DELETE_OK')}
        onOk={handleOnDelete}
        loading={isDeletingUser}
      />
      <TableTools
        placeholder={getTranslations('LIST_SEARCH_IN_TEURONA')}
        onSearch={(value) => setFilterValue([value])}
        onChange={(event) => setFilterValue([event.target.value])}
        onEnable={() => enableStatus()}
        onDisable={() => disableStatus()}
        filter={<CompanyFilter onApply={setFilteredInfo} value={filteredInfo} />}
        activationBtnsVisible={showActivationBtns}
        value={filterValue}
      />
      <Table<IUser>
        loading={areCompaniesFetching || areUsersFetching}
        className={tableStyles.table}
        rowClassName={() => tableStyles.row}
        rowKey="username"
        rowSelection={rowSelection}
        columns={columns}
        dataSource={usersList}
        pagination={{
          position: ["bottomRight"],
          current: page,
          pageSize: PAGE_SIZE,
          total,
          showSizeChanger: false,
          onChange: (newPage) => {
            dispatch(fetchUsersPaginated({
              page: newPage,
              limit: PAGE_SIZE,
              companyId: filteredInfo || undefined,
              text: debouncedText,
            }));
          },
        }}
      />
    </React.Fragment>
  );
};
